import type { UserKpiEntry } from "@nexus/contracts";
import { formatMetricValue } from "@/features/dashboard/lib/format";
import { clampKpiDecimals } from "./kpi-decimals";

/** Ascending by persisted drag index; ties fall back to name for a stable render. */
export function sortUserKpis(entries: UserKpiEntry[]): UserKpiEntry[] {
  return [...entries].sort((a, b) => a.index - b.index || a.name.localeCompare(b.name));
}

export function visibleUserKpis(entries: UserKpiEntry[]): UserKpiEntry[] {
  return sortUserKpis(entries).filter((entry) => !entry.hidden);
}

export function hiddenUserKpis(entries: UserKpiEntry[]): UserKpiEntry[] {
  return sortUserKpis(entries).filter((entry) => entry.hidden);
}

/**
 * The name order to persist after a drag. Order is computed over the full set
 * (hidden rows included) so hiding a KPI never silently rewrites the layout of
 * the ones around it — the reorder endpoint requires every name it knows about.
 */
export function reorderUserKpiNames(
  entries: UserKpiEntry[],
  activeName: string,
  overName: string,
): string[] {
  const names = sortUserKpis(entries).map((entry) => entry.name);
  const from = names.indexOf(activeName);
  const to = names.indexOf(overName);
  if (from === -1 || to === -1 || from === to) return names;

  const next = [...names];
  next.splice(from, 1);
  next.splice(to, 0, activeName);
  return next;
}

/** Optimistic `index` rewrite matching what the server will persist for `names`. */
export function applyUserKpiOrder(
  entries: UserKpiEntry[],
  names: string[],
): UserKpiEntry[] {
  const indexByName = new Map(names.map((name, index) => [name, index]));
  return sortUserKpis(
    entries.map((entry) => {
      const index = indexByName.get(entry.name);
      return index === undefined ? entry : { ...entry, index };
    }),
  );
}

/**
 * Re-derive a KPI's display string from the raw value using the *user's* format
 * and decimals.
 *
 * The server formats `formatted` / `avgFormatted` from the ORG definition, so a
 * user who overrides decimals or format would otherwise see a stale string. The
 * arithmetic mirrors the server's `formatKpiValue` so both agree when the user
 * has not overridden anything.
 */
export function formatUserKpiValue(
  value: number | null | undefined,
  format: UserKpiEntry["format"],
  decimals: number,
  currencySymbol = "$",
  emptyValue = "—",
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return emptyValue;

  const safeDecimals = clampKpiDecimals(decimals);

  switch (format) {
    case "CURRENCY":
      return formatMetricValue(value, "currency", currencySymbol, safeDecimals);
    case "PERCENT":
      return formatMetricValue(value * 100, "percent", currencySymbol, safeDecimals);
    case "NUMBER":
      return formatMetricValue(value, "count", currencySymbol, safeDecimals);
  }
}

/**
 * Whether "Reset to org default" should be offered. An `ORG_SNAPSHOT` row whose
 * source org KPI was deleted is an orphan: still usable, but nothing to reset to.
 */
export function canResetUserKpi(entry: UserKpiEntry): boolean {
  return entry.canResetToOrg;
}

/** Orphaned = came from an org KPI that no longer exists. Never true for a custom row. */
export function isOrphanedUserKpi(entry: UserKpiEntry): boolean {
  return !entry.isCustom && !entry.canResetToOrg;
}
